import React, { useState, useEffect, useMemo } from "react";
import styles from "./Houses.module.css";
import { Link } from "react-router-dom";
import HouseModal from "../components/HouseModal"; // Import the modal component
import { FixedSizeGrid } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

// Helper function for score color coding (Red to Green)
const getScoreColor = (score) => {
  if (score === null || score === undefined || isNaN(score)) {
    return '#555'; // Default grey for missing scores
  }
  // Ensure score is within 0-100
  const clampedScore = Math.max(0, Math.min(100, score));
  // Interpolate hue from red (0) to green (120)
  const hue = (clampedScore / 100) * 120;
  // Use HSL color format (fixed saturation and lightness)
  return `hsl(${hue}, 70%, 45%)`;
};

// LocalStorage utility functions for favorites and ignored houses
const getUserId = () => {
  let userId = localStorage.getItem('houseFinderUserId');
  if (!userId) {
    userId = 'user_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('houseFinderUserId', userId);
  }
  return userId;
};

const getFavorites = () => {
  const userId = getUserId();
  const favorites = localStorage.getItem(`houseFinder_favorites_${userId}`);
  return favorites ? JSON.parse(favorites) : [];
};

const getIgnored = () => {
  const userId = getUserId();
  const ignored = localStorage.getItem(`houseFinder_ignored_${userId}`);
  return ignored ? JSON.parse(ignored) : [];
};

const saveFavorites = (favorites) => {
  const userId = getUserId();
  localStorage.setItem(`houseFinder_favorites_${userId}`, JSON.stringify(favorites));
};

const saveIgnored = (ignored) => {
  const userId = getUserId();
  localStorage.setItem(`houseFinder_ignored_${userId}`, JSON.stringify(ignored));
};

const Houses = () => {
  // Filter states
  const [maxPrice, setMaxPrice] = useState("1000000");
  const [minBedrooms, setMinBedrooms] = useState("4");
  const [stations, setStations] = useState("Shenfield");
  const [propertyType, setPropertyType] = useState([]);
  const [maxDistance, setMaxDistance] = useState("0.5");
  const [minSqft, setMinSqft] = useState("");
  const [location1, setLocation1] = useState("");
  const [location2, setLocation2] = useState("");
  const [maxDuration1, setMaxDuration1] = useState("");
  const [maxDuration2, setMaxDuration2] = useState("");
  const [maxStationDuration, setMaxStationDuration] = useState("");
  const [numFloors, setNumFloors] = useState([]);
  const [buyOrRent, setBuyOrRent] = useState("buy");
  const [propertyTypeDropdownOpen, setPropertyTypeDropdownOpen] = useState(false);
  const [numFloorsDropdownOpen, setNumFloorsDropdownOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  // Favorites and Ignored Houses State
  const [favorites, setFavorites] = useState([]);
  const [ignored, setIgnored] = useState([]);
  const [currentView, setCurrentView] = useState('search'); // 'search' or 'favorites'

  // Data, Loading, Error states
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  // Sorting State
  const [sortBy, setSortBy] = useState("date_added"); // Default sort
  const [sortOrder, setSortOrder] = useState("desc"); // Default order

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHouseIndex, setSelectedHouseIndex] = useState(null);

  // State for active tab on each card { houseId: activeTab }
  const [cardTabState, setCardTabState] = useState({});
  // State for current image index on each card { houseId: imageIndex }
  const [cardImageIndexState, setCardImageIndexState] = useState({});

  const [schoolType, setSchoolType] = useState('primary'); // New state for school type toggle

  const [displayedHouses, setDisplayedHouses] = useState([]);
  const [pageSize] = useState(40); // Increased to 40 houses
  const [currentPage, setCurrentPage] = useState(1);

  // Streaming states
  const [streamingStatus, setStreamingStatus] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [eventSource, setEventSource] = useState(null);

  // Demographics type toggle
  const [demographicsType, setDemographicsType] = useState('ethnicity'); // 'ethnicity' or 'religion'

  // Load favorites and ignored houses from localStorage on component mount
  useEffect(() => {
    setFavorites(getFavorites());
    setIgnored(getIgnored());
  }, []);

  // Functions to handle favorites and ignored houses
  const handleFavorite = (house) => {
    const propertyUrl = house.property_url || house.id;
    const newFavorites = favorites.includes(propertyUrl) 
      ? favorites.filter(url => url !== propertyUrl)
      : [...favorites, propertyUrl];
    
    setFavorites(newFavorites);
    saveFavorites(newFavorites);
  };

  const handleIgnore = (house) => {
    const propertyUrl = house.property_url || house.id;
    const newIgnored = [...ignored, propertyUrl];
    
    setIgnored(newIgnored);
    saveIgnored(newIgnored);
  };

  // Filter houses based on current view and ignored list
  const getFilteredHouses = (housesToFilter) => {
    if (currentView === 'favorites') {
      return housesToFilter.filter(house => 
        favorites.includes(house.property_url || house.id)
      );
    } else {
      // In search view, filter out ignored houses
      return housesToFilter.filter(house => 
        !ignored.includes(house.property_url || house.id)
      );
    }
  };

  // Memoized sorted houses - MOVED BEFORE useEffect
  const sortedHouses = useMemo(() => {
    const filteredHouses = getFilteredHouses(houses);
    let sorted = [...filteredHouses];
    sorted.sort((a, b) => {
      let valA = a[sortBy];
      let valB = b[sortBy];

      // Handle potential null/undefined or non-numeric values for sorting fields
      if (sortBy === "price" || sortBy === "distance_to_station") {
        valA = parseFloat(valA) || 0;
        valB = parseFloat(valB) || 0;
      } else if (sortBy === "date_added") {
        valA = parseInt(valA) || 0;
        valB = parseInt(valB) || 0;
      }
      // Add more specific handling if needed for other fields

      if (valA < valB) {
        return sortOrder === "asc" ? -1 : 1;
      }
      if (valA > valB) {
        return sortOrder === "asc" ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }, [houses, sortBy, sortOrder, currentView, favorites, ignored]);

  // Update displayed houses when houses or sorting changes
  useEffect(() => {
    const start = 0;
    const end = currentPage * pageSize;
    setDisplayedHouses(sortedHouses.slice(start, end));
  }, [sortedHouses, currentPage, pageSize]);

  // Cleanup EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [eventSource]);

  const handleLoadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const handleRunSearch = async () => {
    // Close any existing EventSource
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }

    setLoading(true);
    setIsStreaming(true);
    setError(null);
    setStreamingStatus("Initializing search...");
    setHouses([]);
    setDisplayedHouses([]);
    setCurrentPage(1);
    setCardTabState({});
    setCardImageIndexState({});

    // Ensure stations is treated as a string before splitting
    const stationInput = typeof stations === "string" ? stations : "";
    const stationList = stationInput
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s);

    const params = new URLSearchParams();
    let url;
    
    // If in favorites view, use the url_search endpoint
    if (currentView === 'favorites' && favorites.length > 0) {
      // Use the new URL search endpoint for favorites
      params.append("urls", JSON.stringify(favorites));
      url = `${apiUrl || ""}/url_search?${params.toString()}`;
    } else {
      // Regular search parameters
      params.append("max_price", maxPrice || "1000000");
      params.append("min_bedrooms", minBedrooms || "1");
      params.append("property_type", propertyType.join(",") || "");
      params.append("buy_or_rent", buyOrRent || "buy");
      params.append("num_floors", numFloors.join(",") || "");
      
      // Append new parameters
      params.append("distance_from_station", maxDistance || "0.5");
      
      // Append optional parameters if they have values
      if (minSqft) {
        params.append("min_sqft", minSqft);
      }
      if (location1.trim()) {
        params.append("location1", location1.trim());
      }
      if (location2.trim()) {
        params.append("location2", location2.trim());
      }
      if (maxDuration1) {
        params.append("max_duration1", maxDuration1);
      }
      if (maxDuration2) {
        params.append("max_duration2", maxDuration2);
      }

      params.append("stations", stationList);
      
      // Use the streaming endpoint for regular search
      url = `${apiUrl || ""}/run_house_search_stream?${params.toString()}`;
    }

    try {
      console.log("Streaming from URL:", url);

      const newEventSource = new EventSource(url);
      setEventSource(newEventSource);

      newEventSource.onmessage = function(event) {
        try {
          // Sanitize the JSON string by replacing NaN values with null
          const sanitizedData = event.data.replace(/:\s*NaN/g, ': null');
          const data = JSON.parse(sanitizedData);
          
          switch(data.type) {
            case 'status':
              console.log("Received status:", data.message);
              setStreamingStatus(data.message || "Processing...");
              break;
              
            case 'property':
              console.log("Received property:", data.data?.property_url || "unknown");
              // New property found - add to houses array
              // Check if property data exists before processing
              if (data.data) {
                // Clean up any NaN values in the property data
                const cleanedData = cleanNaNValues(data.data);
                
                const newHouse = {
                  ...cleanedData,
                  images: (() => {
                    let images = [];
                    
                    // Handle new image structure with multiple sizes
                    if (cleanedData.images && Array.isArray(cleanedData.images)) {
                      images = cleanedData.images.map(img => {
                        // Prefer larger sizes for better quality
                        if (img.resizedImageUrls && img.resizedImageUrls.size656x437) {
                          return img.resizedImageUrls.size656x437;
                        } else if (img.resizedImageUrls && img.resizedImageUrls.size476x317) {
                          return img.resizedImageUrls.size476x317;
                        } else {
                          return img.url;
                        }
                      });
                    }
                    
                    // Fallback to old image structure
                    if (images.length === 0) {
                      if (cleanedData.image) images.push(cleanedData.image);
                      if (cleanedData.floorplan && cleanedData.floorplan !== cleanedData.image) {
                        images.push(cleanedData.floorplan);
                      }
                    }
                    
                    return images;
                  })(),
                  id: cleanedData.property_url || `${Date.now()}-${Math.random()}`,
                };
                
                setHouses(prevHouses => {
                  console.log("Adding property to houses array. New total:", prevHouses.length + 1);
                  return [...prevHouses, newHouse];
                });
              } else {
                console.warn("Received property message without property data:", data);
              }
              setStreamingStatus(`Found ${data.total_found || 'new'} properties...`);
              break;
              
            case 'station_complete':
              console.log("Station complete:", data.message);
              setStreamingStatus(data.message || "Station processing completed");
              break;
              
            case 'batch_complete':
              console.log("Batch complete:", data.message);
              setStreamingStatus(data.message || "Batch processing completed");
              break;
              
            case 'complete':
              setStreamingStatus("Search completed!");
              setIsStreaming(false);
              setLoading(false);
              newEventSource.close();
              setEventSource(null);
              setTimeout(() => setStreamingStatus(""), 3000); // Clear status after 3 seconds
              break;
              
            case 'error':
              setError(data.message || "An error occurred during search");
              setStreamingStatus("");
              setIsStreaming(false);
              setLoading(false);
              newEventSource.close();
              setEventSource(null);
              break;
              
            default:
              console.log("Unknown message type:", data.type, data);
          }
        } catch (e) {
          console.error("Error parsing JSON message:", e);
          console.error("Raw event data:", event.data);
          setError("Failed to parse streaming data. Invalid JSON received.");
          setStreamingStatus("");
          setIsStreaming(false);
          setLoading(false);
          newEventSource.close();
          setEventSource(null);
        }
      };

      newEventSource.onerror = function(event) {
        console.error("EventSource error:", event);
        setError("Connection lost. Please try again.");
        setStreamingStatus("");
        setIsStreaming(false);
        setLoading(false);
        newEventSource.close();
        setEventSource(null);
      };

    } catch (e) {
      console.error("Error setting up streaming search:", e);
      setError(`Failed to start search: ${e.message}`);
      setStreamingStatus("");
      setIsStreaming(false);
      setLoading(false);
    }
  };

  const handleCancelSearch = () => {
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
    setLoading(false);
    setIsStreaming(false);
    setStreamingStatus("");
    setError(null);
  };

  const toggleFilters = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    try {
      return new Date(timestamp).toLocaleDateString("en-GB");
    } catch (e) {
      return "Invalid Date";
    }
  };

  // Modal handling functions
  const handleOpenModal = (index) => {
    setSelectedHouseIndex(index);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedHouseIndex(null);
  };

  const handleNextHouse = () => {
    if (
      selectedHouseIndex !== null &&
      selectedHouseIndex < sortedHouses.length - 1
    ) {
      setSelectedHouseIndex(selectedHouseIndex + 1);
    }
  };

  const handlePrevHouse = () => {
    if (selectedHouseIndex !== null && selectedHouseIndex > 0) {
      setSelectedHouseIndex(selectedHouseIndex - 1);
    }
  };

  const currentHouse =
    selectedHouseIndex !== null ? sortedHouses[selectedHouseIndex] : null;

  // Function to handle tab changes on a card
  const handleTabChange = (houseId, tabName) => {
    setCardTabState(prev => ({
      ...prev,
      [houseId]: tabName
    }));
  };

  // Functions to handle image navigation on a card
  const handleNextImage = (e, houseId, imageCount) => {
    e.stopPropagation(); // Prevent card click/modal open
    setCardImageIndexState(prev => {
        const currentIndex = prev[houseId] || 0;
        const nextIndex = (currentIndex + 1) % imageCount;
        return { ...prev, [houseId]: nextIndex };
    });
  };

  const handlePrevImage = (e, houseId, imageCount) => {
    e.stopPropagation(); // Prevent card click/modal open
    setCardImageIndexState(prev => {
        const currentIndex = prev[houseId] || 0;
        const prevIndex = (currentIndex - 1 + imageCount) % imageCount;
        return { ...prev, [houseId]: prevIndex };
    });
  };

  // Helper function to calculate average school score
  const calculateAverageSchoolScore = (house) => {
    const primaryScores = [];
    const secondaryScores = [];
    
    // Collect primary school scores
    for (let i = 1; i <= 5; i++) {
      const score = house[`primary_school_${i}_percentile`];
      if (score !== null && score !== undefined) {
        primaryScores.push(score);
      }
    }
    
    // Collect secondary school scores
    for (let i = 1; i <= 5; i++) {
      const score = house[`secondary_school_${i}_percentile`];
      if (score !== null && score !== undefined) {
        secondaryScores.push(score);
      }
    }
    
    // Calculate averages
    const primaryAvg = primaryScores.length > 0 
      ? primaryScores.reduce((a, b) => a + b, 0) / primaryScores.length 
      : null;
    const secondaryAvg = secondaryScores.length > 0 
      ? secondaryScores.reduce((a, b) => a + b, 0) / secondaryScores.length 
      : null;
    
    // Return average of both if both exist, otherwise return whichever exists
    if (primaryAvg !== null && secondaryAvg !== null) {
      return (primaryAvg + secondaryAvg) / 2;
    }
    return primaryAvg !== null ? primaryAvg : secondaryAvg;
  };

  // Format price with commas
  const formatPrice = (value) => {
    const numericValue = value.replace(/,/g, '');
    if (numericValue === '') return '';
    return parseInt(numericValue).toLocaleString();
  };

  const handlePriceChange = (e) => {
    const value = e.target.value.replace(/,/g, '');
    if (value === '' || /^\d+$/.test(value)) {
      setMaxPrice(value);
    }
  };

  // Handle property type selection
  const handlePropertyTypeChange = (type) => {
    setPropertyType(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  // Select all property types
  const selectAllPropertyTypes = () => {
    setPropertyType(["Detached", "Semi-Detached", "Terraced", "Flats", "Bungalow"]);
  };

  // Clear all property types
  const clearAllPropertyTypes = () => {
    setPropertyType([]);
  };

  // Handle number of floors selection
  const handleNumFloorsChange = (floor) => {
    setNumFloors(prev => {
      if (prev.includes(floor)) {
        return prev.filter(f => f !== floor);
      } else {
        return [...prev, floor];
      }
    });
  };

  // Select all floor options
  const selectAllFloors = () => {
    setNumFloors(["1", "2", "3+"]);
  };

  // Clear all floor options
  const clearAllFloors = () => {
    setNumFloors([]);
  };

  // Helper function to clean up NaN values in object
  const cleanNaNValues = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    
    const cleaned = Array.isArray(obj) ? [] : {};
    
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (typeof value === 'number' && isNaN(value)) {
          cleaned[key] = null; // Replace NaN with null
        } else if (typeof value === 'object' && value !== null) {
          cleaned[key] = cleanNaNValues(value); // Recursively clean nested objects
        } else {
          cleaned[key] = value;
        }
      }
    }
    
    return cleaned;
  };

  // Grid cell renderer
  const Cell = ({ columnIndex, rowIndex, style }) => {
    const index = rowIndex * 4 + columnIndex;
    const house = displayedHouses[index];
    
    if (!house) return null;

    const fallbackImage = "https://placehold.co/600x400/222/fff?text=No+Image";
    const activeTab = cardTabState[house.id] || 'Overview';
    
    // Calculate average school score
    const schoolScore = calculateAverageSchoolScore(house);
    const schoolScoreColor = getScoreColor(schoolScore);
    
    // Crime Score - use new field
    const crimeScore = house.local_region_crime_percentile || house.crime_score;
    const crimeScoreColor = getScoreColor(crimeScore);
    
    // New score badges
    const deprivationScore = house.local_region_deprivation_percentile;
    const deprivationScoreColor = getScoreColor(deprivationScore);
    
    const incomeScore = 100 -house.local_region_income_percentile;
    const incomeScoreColor = getScoreColor(incomeScore);
    
    // Image gallery logic
    const currentImageIndex = cardImageIndexState[house.id] || 0;
    const images = house.images || [];
    const imageCount = images.length;
    const currentImageUrl = images[currentImageIndex] || fallbackImage;

    return (
      <div style={style} className={styles.gridCell}>
        <div className={styles.houseCard}>
          <div className={styles.houseImageContainer}> 
            <img
              src={currentImageUrl}
              alt={`House image ${currentImageIndex + 1} of ${imageCount}`}
              className={styles.houseImage}
              onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
              onClick={() => handleOpenModal(index)}
            />
            
            {/* Favorite and Ignore Buttons */}
            <div className={styles.actionButtonsContainer}>
              <button 
                className={`${styles.actionButton} ${styles.favoriteButton} ${
                  favorites.includes(house.property_url || house.id) ? styles.favoriteActive : ''
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFavorite(house);
                }}
                title={favorites.includes(house.property_url || house.id) ? "Remove from favorites" : "Add to favorites"}
              >
                ❤️
              </button>
              <button 
                className={`${styles.actionButton} ${styles.ignoreButton}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleIgnore(house);
                }}
                title="Ignore this house"
              >
                ❌
              </button>
            </div>
            
            {imageCount > 1 && (
              <>
                <button 
                  className={`${styles.imageNavButton} ${styles.imagePrevButton}`}
                  onClick={(e) => handlePrevImage(e, house.id, imageCount)}
                >
                  &larr;
                </button>
                <button 
                  className={`${styles.imageNavButton} ${styles.imageNextButton}`}
                  onClick={(e) => handleNextImage(e, house.id, imageCount)}
                >
                  &rarr;
                </button>
              </>
            )}
            <div className={styles.scoreBadgesContainer}>
              {schoolScore !== null && schoolScore !== undefined && (
                <div className={styles.scoreBadge} style={{ backgroundColor: schoolScoreColor }}>
                  School Score: {Math.round(schoolScore)}
                </div>
              )}
              {crimeScore !== null && crimeScore !== undefined && (
                <div className={styles.scoreBadge} style={{ backgroundColor: crimeScoreColor }}>
                  Crime Score: {Math.round(crimeScore)}
                </div>
              )}
              {deprivationScore !== null && deprivationScore !== undefined && (
                <div className={styles.scoreBadge} style={{ backgroundColor: deprivationScoreColor }}>
                  Deprivation Score: {Math.round(deprivationScore)}
                </div>
              )}
              {incomeScore !== null && incomeScore !== undefined && (
                <div className={styles.scoreBadge} style={{ backgroundColor: incomeScoreColor }}>
                  Income Score: {Math.round(incomeScore)}
                </div>
              )}
            </div>
          </div>

          {/* Card Tabs Navigation */}
          <div className={styles.cardTabs}>
            <button 
              className={`${styles.tabButton} ${activeTab === 'Overview' ? styles.tabActive : ''}`}
              onClick={() => handleTabChange(house.id, 'Overview')}
            >
              Overview
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'Schools' ? styles.tabActive : ''}`}
              onClick={() => handleTabChange(house.id, 'Schools')}
            >
              Schools
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'Demographics' ? styles.tabActive : ''}`}
              onClick={() => handleTabChange(house.id, 'Demographics')}
            >
              Demographics
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'Description' ? styles.tabActive : ''}`} 
              onClick={() => handleTabChange(house.id, 'Description')}
            >
              Description 
            </button>
          </div>

          {/* Tab Content */}
          <div className={styles.tabContent}>
            {activeTab === 'Overview' && (
              <div className={styles.houseInfo}>
                <h3 className={styles.houseTitle}>
                  {house.bedrooms} bed {house['property type']} in {house.station_name || house.station}
                </h3>
                <p className={styles.housePrice}>
                  {house.price ? house.price.toLocaleString() : "N/A"}{buyOrRent === 'rent' ? ' PCM' : ''}
                </p>
                <p className={styles.dateAdded}>
                  {house.is_reduction ? 'Reduced On' : 'Added'}: {formatDate(house.date_added)}
                </p>
                <div className={styles.houseDetails}>
                  <span>🛏️ {house.bedrooms || "N/A"} Bedrooms</span>
                  <span>🚿 {house.bathrooms || "N/A"} Bathrooms</span>
                  <span>🏢 {house.number_of_floors || "?"} Floors</span>
                  <span>📏 {house.sqft ? `${house.sqft.toLocaleString()} sqft` : house.size ? `${house.size.toLocaleString()} sqft` : "Size Not Listed"}</span>
                  <span>🚶 {house.distance_to_station ? `${house.distance_to_station.toFixed(2)} miles from station` : "N/A dist"}</span>
                  {house.journey1_duration && (
                    <span className={styles.journeyTime}>➡️ PC1: {house.journey1_duration} mins</span>
                  )}
                  {house.journey2_duration && (
                    <span className={styles.journeyTime}>➡️ PC2: {house.journey2_duration} mins</span>
                  )}
                </div>
                <button className={styles.viewDetailsButton} onClick={() => handleOpenModal(index)}> 
                  View More Details
                </button>
              </div>
            )}

            {activeTab === 'Schools' && (
              <div className={styles.schoolsInfo}>
                <h4>
                  Nearby {schoolType === 'primary' ? 'Primary' : 'Secondary'} Schools
                  <div className={styles.schoolTypeToggle}>
                    <button 
                      className={`${styles.toggleButton} ${schoolType === 'primary' ? styles.toggleActive : ''}`}
                      onClick={() => setSchoolType('primary')}
                    >
                      Primary
                    </button>
                    <button 
                      className={`${styles.toggleButton} ${schoolType === 'secondary' ? styles.toggleActive : ''}`}
                      onClick={() => setSchoolType('secondary')}
                    >
                      Secondary
                    </button>
                  </div>
                </h4>
                <ul className={styles.schoolsList}>
                  {[...Array(5)].map((_, i) => {
                    const schoolName = house[`${schoolType}_school_${i+1}_name`];
                    const schoolDistance = house[`${schoolType}_school_${i+1}_distance`];
                    const schoolScore = house[`${schoolType}_school_${i+1}_percentile`];
                    
                    if (!schoolName) return null;
                    
                    return (
                      <li key={i} className={styles.schoolItem}>
                        <div className={styles.schoolInfo}>
                          <span className={styles.schoolName}>
                            {schoolName}
                            <span className={styles.schoolDistance}>{schoolDistance.toFixed(1)}km</span>
                          </span>
                        </div>
                        <span 
                          className={styles.schoolScoreItemBadge}
                          style={{ backgroundColor: getScoreColor(schoolScore) }}
                        >
                          {schoolScore !== null && schoolScore !== undefined ? Math.round(schoolScore) : 'N/A'}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <button className={styles.viewDetailsButton} onClick={() => handleOpenModal(index)}> 
                  View More Details
                </button>
              </div>
            )}

            {activeTab === 'Demographics' && (
              <div className={styles.demographicsInfo}>
                <h4>Local Demographics</h4>
                <div className={styles.demographicsToggle}>
                  <button 
                    className={`${styles.toggleButton} ${demographicsType === 'ethnicity' ? styles.toggleActive : ''}`}
                    onClick={() => setDemographicsType('ethnicity')}
                  >
                    Ethnicity
                  </button>
                  <button 
                    className={`${styles.toggleButton} ${demographicsType === 'religion' ? styles.toggleActive : ''}`}
                    onClick={() => setDemographicsType('religion')}
                  >
                    Religion
                  </button>
                </div>
                <div className={styles.demographicsList}>
                  {demographicsType === 'ethnicity' && (
                    <>
                      {[
                        { key: 'white_british', label: 'White British' },
                        { key: 'other_white', label: 'Other White' },
                        { key: 'south_asian', label: 'South Asian' },
                        { key: 'east_asian', label: 'East Asian' },
                        { key: 'black', label: 'Black' },
                        { key: 'mixed', label: 'Mixed' },
                        { key: 'arab', label: 'Arab' }
                      ].map(({ key, label }) => (
                        <div key={key} className={styles.demographicItem}>
                          <span className={styles.demographicLabel}>{label}</span>
                          <div className={styles.demographicBar}>
                            <div 
                              className={styles.demographicBarFill}
                              style={{ width: `${house[`ethnicity_${key}`] || 0}%` }}
                            />
                          </div>
                          <span className={styles.demographicPercentage}>
                            {house[`ethnicity_${key}`] ? `${Math.round(house[`ethnicity_${key}`])}%` : 'N/A'}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                  {demographicsType === 'religion' && (
                    <>
                      {[
                        { key: 'christian', label: 'Christian' },
                        { key: 'muslim', label: 'Muslim' },
                        { key: 'hindu', label: 'Hindu' },
                        { key: 'buddhist', label: 'Buddhist' },
                        { key: 'sikh', label: 'Sikh' },
                        { key: 'jewish', label: 'Jewish' },
                        { key: 'other_religion', label: 'Other Religion' },
                        { key: 'no_religion', label: 'No Religion' }
                      ].map(({ key, label }) => (
                        <div key={key} className={styles.demographicItem}>
                          <span className={styles.demographicLabel}>{label}</span>
                          <div className={styles.demographicBar}>
                            <div 
                              className={styles.demographicBarFill}
                              style={{ width: `${house[`local_region_${key}`] || 0}%` }}
                            />
                          </div>
                          <span className={styles.demographicPercentage}>
                            {house[`local_region_${key}`] ? `${Math.round(house[`local_region_${key}`])}%` : 'N/A'}
                          </span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
                <button className={styles.viewDetailsButton} onClick={() => handleOpenModal(index)}> 
                  View More Details
                </button>
              </div>
            )}

            {activeTab === 'Description' && (
               <div className={styles.descriptionInfo}> {/* Changed from crimeInfo */} 
                  <h4>Property Description</h4>
                  <p dangerouslySetInnerHTML={{ __html: house.description || 'No description available.' }}></p>
                  <button className={styles.viewDetailsButton} onClick={() => handleOpenModal(index)}> 
                    View More Details
                  </button>
               </div>
            )}
          </div> 
        </div>
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Link to="/ratedtools" className={styles.backButton}>
            <span>←</span> Back to Tools
          </Link>
          <h1 className={styles.title}>UK House Finder</h1>
          
          {/* View Toggle Buttons */}
          <div className={styles.viewToggle}>
            <button 
              className={`${styles.viewToggleButton} ${currentView === 'search' ? styles.viewToggleActive : ''}`}
              onClick={() => setCurrentView('search')}
            >
              Search Results ({getFilteredHouses(houses).length})
            </button>
            <button 
              className={`${styles.viewToggleButton} ${currentView === 'favorites' ? styles.viewToggleActive : ''}`}
              onClick={() => setCurrentView('favorites')}
            >
              Favorites ({favorites.length})
            </button>
          </div>
          
          <button className={styles.filterToggle} onClick={toggleFilters}>
            {isFilterOpen ? "Hide Filters" : "Show Filters"}
          </button>
        </div>

        {currentView === 'search' && (
          <div
            className={`${styles.filterSection} ${
              isFilterOpen ? styles.filterOpen : ""
            }`}
          >
            <button 
              className={styles.collapseButton}
              onClick={toggleFilters}
              title={isFilterOpen ? "Hide Filters" : "Show Filters"}
            >
              {isFilterOpen ? "−" : "+"}
            </button>

            {!isFilterOpen && (
              <div className={styles.collapsedHeader}>
                <h3>Filters</h3>
                <button 
                  className={styles.expandButton}
                  onClick={toggleFilters}
                >
                  Show Filters
                </button>
              </div>
            )}

            {/* House Filters */}
            <div className={styles.houseFiltersSection}>
              <h3 className={styles.filterSectionTitle}>House Filters</h3>
              <div className={styles.houseFiltersGrid}>
                <div className={styles.filterGroup}>
                  <label htmlFor="buyOrRent">Buy or Rent</label>
                  <select
                    id="buyOrRent"
                    value={buyOrRent}
                    onChange={(e) => setBuyOrRent(e.target.value)}
                    className={styles.select}
                  >
                    <option value="buy">Buy</option>
                    <option value="rent">Rent</option>
                  </select>
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="maxPrice">Max Price (£)</label>
                  <input
                    type="text"
                    id="maxPrice"
                    value={formatPrice(maxPrice)}
                    onChange={handlePriceChange}
                    placeholder="Max Price"
                    className={styles.input}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="minBedrooms">Min Bedrooms</label>
                  <input
                    type="number"
                    id="minBedrooms"
                    value={minBedrooms}
                    onChange={(e) => setMinBedrooms(e.target.value)}
                    placeholder="Min Bedrooms"
                    className={styles.input}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="numFloors">Number of Floors</label>
                  <div className={styles.dropdownContainer}>
                    <button
                      type="button"
                      className={styles.dropdownButton}
                      onClick={() => setNumFloorsDropdownOpen(!numFloorsDropdownOpen)}
                    >
                      {numFloors.length === 0 ? 'All Floors' : 
                       numFloors.length === 3 ? 'All Floors' : 
                       numFloors.join(', ')}
                      <span className={styles.dropdownArrow}>▼</span>
                    </button>
                    {numFloorsDropdownOpen && (
                      <div className={styles.dropdownMenu}>
                        <div className={styles.dropdownHeader}>
                          <button
                            type="button"
                            onClick={selectAllFloors}
                            className={styles.dropdownActionButton}
                          >
                            Select All
                          </button>
                          <button
                            type="button"
                            onClick={clearAllFloors}
                            className={styles.dropdownActionButton}
                          >
                            Clear All
                          </button>
                        </div>
                        {["1", "2", "3+"].map((floor) => (
                          <label key={floor} className={styles.dropdownCheckbox}>
                            <input
                              type="checkbox"
                              checked={numFloors.includes(floor)}
                              onChange={() => handleNumFloorsChange(floor)}
                            />
                            <span>{floor}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="minSqft">Minimum House Size (sqft)</label>
                  <input
                    type="number"
                    id="minSqft"
                    value={minSqft}
                    onChange={(e) => setMinSqft(e.target.value)}
                    placeholder="e.g., 1000"
                    min="0"
                    className={styles.input}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="propertyType">Property Type</label>
                  <div className={styles.dropdownContainer}>
                    <button
                      type="button"
                      className={styles.dropdownButton}
                      onClick={() => setPropertyTypeDropdownOpen(!propertyTypeDropdownOpen)}
                    >
                      {propertyType.length === 0 ? 'All Property Types' : 
                       propertyType.length === 4 ? 'All Property Types' : 
                       propertyType.join(', ')}
                      <span className={styles.dropdownArrow}>▼</span>
                    </button>
                    {propertyTypeDropdownOpen && (
                      <div className={styles.dropdownMenu}>
                        <div className={styles.dropdownHeader}>
                          <button
                            type="button"
                            onClick={selectAllPropertyTypes}
                            className={styles.dropdownActionButton}
                          >
                            Select All
                          </button>
                          <button
                            type="button"
                            onClick={clearAllPropertyTypes}
                            className={styles.dropdownActionButton}
                          >
                            Clear All
                          </button>
                        </div>
                        {["Detached", "Semi-Detached", "Terraced", "Flats", "Bungalow"].map((type) => (
                          <label key={type} className={styles.dropdownCheckbox}>
                            <input
                              type="checkbox"
                              checked={propertyType.includes(type)}
                              onChange={() => handlePropertyTypeChange(type)}
                            />
                            <span>{type}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Location Filters */}
            <div className={styles.locationFiltersSection}>
              <h3 className={styles.filterSectionTitle}>Location Filters</h3>
              <div className={styles.locationFiltersGrid}>
                <div className={styles.filterGroup}>
                  <label htmlFor="stations">Stations (Optional)</label>
                  <input
                    type="text"
                    id="stations"
                    value={stations}
                    onChange={(e) => setStations(e.target.value)}
                    placeholder="e.g., Shenfield, Brentwood"
                    className={styles.input}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="location1">Location 1 (Optional)</label>
                  <input
                    type="text"
                    id="location1"
                    value={location1}
                    onChange={(e) => setLocation1(e.target.value)}
                    placeholder="Enter Postcode"
                    className={styles.input}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="maxDuration1">Max Journey Time to Location 1 (mins)</label>
                  <input
                    type="number"
                    id="maxDuration1"
                    value={maxDuration1}
                    onChange={(e) => setMaxDuration1(e.target.value)}
                    placeholder="e.g., 45"
                    min="0"
                    className={styles.input}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="location2">Location 2 (Optional)</label>
                  <input
                    type="text"
                    id="location2"
                    value={location2}
                    onChange={(e) => setLocation2(e.target.value)}
                    placeholder="Enter Postcode"
                    className={styles.input}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="maxDuration2">Max Journey Time to Location 2 (mins)</label>
                  <input
                    type="number"
                    id="maxDuration2"
                    value={maxDuration2}
                    onChange={(e) => setMaxDuration2(e.target.value)}
                    placeholder="e.g., 60"
                    min="0"
                    className={styles.input}
                  />
                </div>

                <div className={styles.filterGroup}>
                  <label htmlFor="maxDistance">Max Distance From Station (miles)</label>
                  <input
                    type="number"
                    id="maxDistance"
                    value={maxDistance}
                    onChange={(e) => setMaxDistance(e.target.value)}
                    placeholder="e.g., 0.5"
                    step="0.1"
                    min="0"
                    className={styles.input}
                  />
                </div>
              </div>
            </div>

            <div className={styles.runButtonContainer}>
              <button
                onClick={handleRunSearch}
                className={styles.runButton}
                disabled={loading}
              >
                {loading ? "Searching..." : "Run Search"}
              </button>

              {isStreaming && (
                <button
                  onClick={handleCancelSearch}
                  className={styles.cancelButton}
                >
                  Cancel Search
                </button>
              )}
            </div>
          </div>
        )}

        {currentView === 'favorites' && (
          <div className={styles.favoritesInfo}>
            <h3>Your Favorite Properties</h3>
            {favorites.length === 0 ? (
              <p>You haven't favorited any properties yet. Switch to Search Results and click the ❤️ button on properties you like!</p>
            ) : (
              <div>
                <p>You have {favorites.length} favorite properties. Click "Run Search" to refresh data for your favorites.</p>
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                  <button
                    onClick={handleRunSearch}
                    className={styles.runButton}
                    disabled={loading}
                  >
                    {loading ? "Refreshing Favorites..." : "Refresh Favorites Data"}
                  </button>
                  {isStreaming && (
                    <button
                      onClick={handleCancelSearch}
                      className={styles.cancelButton}
                    >
                      Cancel Search
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {(loading || isStreaming) && (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <span>{streamingStatus || "Loading houses..."}</span>
          </div>
        )}
        {error && <div className={styles.error}>{error}</div>}

        {!loading && !error && sortedHouses.length > 0 && (
          <>
            <div className={styles.resultsHeader}>
              <span className={styles.resultsCount}>
                Found {sortedHouses.length} properties
                {currentView === 'favorites' ? ' (favorites)' : ''}
              </span>
              <div className={styles.sortControls}>
                <label htmlFor="sortBy">Sort by:</label>
                <select
                  id="sortBy"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className={styles.sortSelect}
                >
                  <option value="date_added">Date Listed</option>
                  <option value="price">Price</option>
                  <option value="distance_to_station">
                    Distance from Station
                  </option>
                  <option value="schoolScore">School Score</option>
                  <option value="crime_score">Crime Score</option>
                  <option value="size">Size</option>
                  <option value="journey1_duration">Journey Time to Location 1</option>
                  <option value="journey2_duration">Journey Time to Location 2</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className={styles.sortSelect}
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>

            <div className={styles.virtualizedGrid}>
              <AutoSizer>
                {({ height, width }) => {
                  const columnCount = Math.min(4, Math.floor(width / 280));
                  const rowCount = Math.ceil(displayedHouses.length / columnCount);
                  const columnWidth = width / columnCount;
                  const rowHeight = 560;

                  return (
                    <FixedSizeGrid
                      columnCount={columnCount}
                      columnWidth={columnWidth}
                      height={height}
                      rowCount={rowCount}
                      rowHeight={rowHeight}
                      width={width}
                    >
                      {Cell}
                    </FixedSizeGrid>
                  );
                }}
              </AutoSizer>
            </div>

            {displayedHouses.length < sortedHouses.length && (
              <div className={styles.loadMoreContainer}>
                <button 
                  className={styles.loadMoreButton}
                  onClick={handleLoadMore}
                >
                  Load More ({sortedHouses.length - displayedHouses.length} more properties)
                </button>
              </div>
            )}
          </>
        )}

        {!loading && !error && currentView === 'favorites' && houses.length === 0 && favorites.length > 0 && (
          <div className={styles.noResults}>
            <h3>No Data Loaded</h3>
            <p>Click "Refresh Favorites Data" above to load information for your {favorites.length} favorite properties.</p>
          </div>
        )}

        {!loading && !error && sortedHouses.length === 0 && houses.length > 0 && (
          <div className={styles.noResults}>
            <h3>No Properties Match Your Criteria</h3>
            <p>Try adjusting your filters or search criteria.</p>
          </div>
        )}
      </div>

      {isModalOpen && currentHouse && (
        <HouseModal
          house={currentHouse}
          onClose={handleCloseModal}
          onNext={handleNextHouse}
          onPrev={handlePrevHouse}
          hasNext={selectedHouseIndex < sortedHouses.length - 1}
          hasPrev={selectedHouseIndex > 0}
        />
      )}
    </div>
  );
};

export default Houses;
